/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Traduzir os erros do GoTrue para frases que apontam na direção certa
 *
 *  Porque não é tudo "credenciais inválidas"
 *
 *  Era. E custou uma tarde: uma conta com o e-mail POR CONFIRMAR recebia
 *  "Credenciais inválidas", exatamente a mesma frase de uma palavra-passe
 *  errada. A pessoa reescreve a palavra-passe cinco vezes, pede uma nova ao
 *  administrador, e o problema estava noutro sítio.
 *
 *  Uma mensagem de erro que descreve a causa errada é pior do que uma mensagem
 *  genérica: manda a pessoa investigar na direção oposta.
 *
 *  No LOGIN continua a NÃO se distinguir "e-mail não existe" de "palavra-passe
 *  errada" — isso é deliberado, e é o que impede alguém de usar o ecrã de login
 *  para descobrir quem tem conta. O e-mail por confirmar é diferente: quem o vê
 *  já provou saber a palavra-passe daquela conta.
 *
 *  ⚠ O "email rate limit exceeded" é o erro do ecrã de CRIAR CONTA: cada
 *  registo faz o Supabase enviar um e-mail de confirmação pelo SMTP interno de
 *  desenvolvimento, que tem uma quota de poucos envios POR HORA. Esgotada a
 *  quota, QUALQUER registo é recusado com essa frase — temporariamente. É
 *  distinto do "over_request_rate_limit" (que é por minuto): dizer "aguarde um
 *  minuto" aqui mandaria a pessoa tentar cedo demais e voltar a gastar a quota.
 *  Por isso este ramo vem ANTES do genérico de "rate limit".
 * ─────────────────────────────────────────────────────────────────────────────
 */

type ContextoAuth = "login" | "cadastro";

export function mensagemDeErro(
  error: { message?: string; code?: string },
  contexto: ContextoAuth,
): string {
  if (error.message === "FALHA_TECNICA") {
    return "O servidor não respondeu a tempo. Tentar de novo.";
  }

  const codigo = error.code ?? "";
  const texto = (error.message ?? "").toLowerCase();

  if (codigo === "email_not_confirmed" || texto.includes("email not confirmed")) {
    return "A palavra-passe está certa, mas o e-mail ainda não foi confirmado. Procure a mensagem de confirmação, ou peça a um administrador que confirme a conta.";
  }
  // Limite de e-mails de confirmação (por hora) — antes do "rate limit" genérico.
  if (codigo === "over_email_send_rate_limit" || texto.includes("email rate limit")) {
    return contexto === "cadastro"
      ? "Foram enviados demasiados e-mails de confirmação há pouco. Aguarde alguns minutos e tente de novo — ou peça a um administrador para criar/confirmar a conta."
      : "O serviço de e-mail atingiu o limite por agora. Aguarde alguns minutos e tente de novo.";
  }
  if (codigo === "over_request_rate_limit" || texto.includes("rate limit")) {
    return "Demasiadas tentativas. Aguarde um minuto antes de tentar de novo.";
  }
  if (
    codigo === "user_already_exists" ||
    codigo === "email_exists" ||
    texto.includes("already registered") ||
    texto.includes("already been registered")
  ) {
    return "Já existe uma conta com este e-mail.";
  }
  if (codigo === "weak_password" || texto.includes("password should be at least")) {
    return "A palavra-passe tem de ter pelo menos 8 caracteres.";
  }
  if (codigo === "user_banned" || texto.includes("banned")) {
    return "Esta conta está bloqueada. Fale com um administrador do programa.";
  }

  return contexto === "cadastro"
    ? "Não foi possível criar a conta."
    : "Credenciais inválidas.";
}
